import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';

const { FiX, FiUser, FiMail, FiPhone, FiBuilding, FiGlobe, FiExternalLink, FiSave, FiTrash2, FiMessageCircle } = FiIcons;

function ContactEditModal({ contact, companyName, onClose, onSave, onDelete }) {
  // Common freemail providers to exclude from auto-domain extraction
  const FREEMAILS = [
    'gmail.com', 'googlemail.com', 'gmx.de', 'gmx.net', 'web.de', 
    'yahoo.com', 'yahoo.de', 'hotmail.com', 'outlook.com', 
    'outlook.de', 't-online.de', 'icloud.com', 'me.com', 
    'mac.com', 'freenet.de', 'live.de', 'live.com'
  ];

  const [formData, setFormData] = useState({
    gender: 'n',
    useDu: false,
    greetingType: 'formal',
    website: '',
    ...contact
  });

  // Auto-fill website from email domain if website is empty and not a freemail
  useEffect(() => {
    if (formData.email && !formData.website && formData.email.includes('@')) {
      const domain = formData.email.split('@')[1].toLowerCase();
      if (!FREEMAILS.includes(domain)) {
        setFormData(prev => ({ ...prev, website: `www.${domain}` }));
      }
    }
  }, [formData.email]);

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  const openWebsite = (url) => {
    if (!url) return;
    const formattedUrl = url.startsWith('http') ? url : `https://${url}`;
    window.open(formattedUrl, '_blank', 'noopener,noreferrer');
  };

  return (
    <motion.div 
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm" 
      onClick={onClose}
    >
      <motion.div 
        initial={{ scale: 0.95, opacity: 0, y: 20 }}
        animate={{ scale: 1, opacity: 1, y: 0 }}
        exit={{ scale: 0.95, opacity: 0, y: 20 }}
        className="bg-white rounded-2xl shadow-2xl w-full max-w-xl overflow-hidden" 
        onClick={e => e.stopPropagation()}
      >
        <div className="p-6 border-b border-gray-100 flex items-center justify-between bg-gray-50">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-600 rounded-xl flex items-center justify-center text-white">
              <SafeIcon icon={FiUser} />
            </div>
            <div>
              <h3 className="font-bold text-gray-900">Kontakt-Profil konfigurieren</h3>
              <p className="text-xs text-gray-500">Personalisierung & Unternehmensdaten</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-200 rounded-full transition-colors cursor-pointer">
            <SafeIcon icon={FiX} className="text-gray-400" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-5">
          {/* Sektion 1: Stammdaten */}
          <div className="grid grid-cols-2 gap-4">
            {companyName && (
               <div className="col-span-2 mb-1 p-3 bg-blue-50/50 border border-blue-100 rounded-xl flex items-center gap-3">
                 <div className="p-2 bg-white rounded-lg text-blue-600 shadow-sm border border-blue-50">
                   <SafeIcon icon={FiBuilding} />
                 </div>
                 <div>
                   <p className="text-[10px] font-black text-blue-400 uppercase tracking-wider">Firma / Zugehörigkeit</p>
                   <p className="font-black text-gray-900 text-sm">{companyName}</p>
                 </div>
               </div>
            )}

            <div className="col-span-2">
              <label className="text-[10px] font-black text-gray-400 uppercase mb-1 block">Name</label>
              <div className="relative">
                <SafeIcon icon={FiUser} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input 
                  type="text" 
                  value={formData.name || ''} 
                  onChange={e => setFormData({ ...formData, name: e.target.value })} 
                  className="w-full pl-10 pr-4 py-2 bg-gray-50 border border-gray-200 rounded-lg outline-none focus:border-blue-500 font-bold"
                  placeholder="Vor- und Nachname"
                />
              </div>
            </div>
            
            <div>
              <label className="text-[10px] font-black text-gray-400 uppercase mb-1 block">E-Mail</label>
              <div className="relative">
                <SafeIcon icon={FiMail} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input 
                  type="email" 
                  value={formData.email || ''} 
                  onChange={e => setFormData({ ...formData, email: e.target.value })} 
                  className="w-full pl-10 pr-4 py-2 bg-gray-50 border border-gray-200 rounded-lg outline-none focus:border-blue-500 font-medium"
                />
              </div>
            </div>
            <div>
              <label className="text-[10px] font-black text-gray-400 uppercase mb-1 block">Telefon</label>
              <div className="relative">
                <SafeIcon icon={FiPhone} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input 
                  type="text" 
                  value={formData.phone || ''} 
                  onChange={e => setFormData({ ...formData, phone: e.target.value })} 
                  className="w-full pl-10 pr-4 py-2 bg-gray-50 border border-gray-200 rounded-lg outline-none focus:border-blue-500 font-medium"
                />
              </div>
            </div>

            <div className="col-span-2">
              <label className="text-[10px] font-black text-gray-400 uppercase mb-1 block">Webseite</label>
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <SafeIcon icon={FiGlobe} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input 
                    type="text" 
                    value={formData.website || ''} 
                    onChange={e => setFormData({ ...formData, website: e.target.value })} 
                    className="w-full pl-10 pr-4 py-2 bg-gray-50 border border-gray-200 rounded-lg outline-none focus:border-blue-500 font-medium"
                    placeholder="www.beispiel.de"
                  />
                </div>
                {formData.website && (
                  <button 
                    type="button"
                    onClick={() => openWebsite(formData.website)}
                    className="px-4 py-2 bg-gray-900 text-white rounded-lg hover:bg-black transition-all flex items-center gap-2 shadow-sm cursor-pointer"
                  >
                    <SafeIcon icon={FiExternalLink} />
                    <span className="text-xs font-bold uppercase tracking-wider">Öffnen</span>
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Sektion 2: Deutsche Anrede-Logik */}
          <div className="bg-blue-50/50 p-4 rounded-xl border border-blue-100 space-y-4">
            <div className="flex items-center gap-2 text-blue-800 font-bold text-xs uppercase tracking-wider mb-2">
              <SafeIcon icon={FiMessageCircle} /> Anrede & Stil (Deutsch)
            </div>
            
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="text-[9px] font-black text-gray-400 uppercase mb-1 block">Anrede</label>
                <select 
                  value={formData.gender}
                  onChange={e => setFormData({ ...formData, gender: e.target.value })}
                  className="w-full px-3 py-2 bg-white border border-gray-200 rounded-lg text-sm font-bold outline-none focus:border-blue-500"
                  disabled={formData.greetingType === 'team'}
                >
                  <option value="n">Unbekannt</option>
                  <option value="m">Herr</option>
                  <option value="f">Frau</option>
                  <option value="d">Divers</option>
                </select>
              </div>

              <div>
                <label className="text-[9px] font-black text-gray-400 uppercase mb-1 block">Form</label>
                <select 
                  value={formData.useDu ? 'du' : 'sie'}
                  onChange={e => setFormData({ ...formData, useDu: e.target.value === 'du' })}
                  className="w-full px-3 py-2 bg-white border border-gray-200 rounded-lg text-sm font-bold outline-none focus:border-blue-500"
                  disabled={formData.greetingType === 'team'}
                >
                  <option value="sie">Sie (Formell)</option>
                  <option value="du">Du (Informell)</option>
                </select>
              </div>

              <div>
                <label className="text-[9px] font-black text-gray-400 uppercase mb-1 block">Begrüßung</label>
                <select 
                  value={formData.greetingType}
                  onChange={e => setFormData({ ...formData, greetingType: e.target.value })}
                  className="w-full px-3 py-2 bg-white border border-gray-200 rounded-lg text-sm font-bold outline-none focus:border-blue-500"
                >
                  <option value="formal">Sehr geehrte/r</option>
                  <option value="casual">Hallo</option>
                  <option value="personal">Liebe/r</option>
                  <option value="team">Liebes Team</option>
                </select>
              </div>
            </div>

            <div className="text-[10px] text-blue-600 bg-white p-2 rounded border border-blue-100 italic font-bold">
              Vorschau: {previewSalutation(formData)}
            </div>
          </div>

          <div className="flex items-center justify-between pt-2">
            {contact.id ? (
              <button 
                type="button" 
                onClick={() => { if(confirm("Kontakt wirklich löschen?")) onDelete(contact.id); }}
                className="px-4 py-2 text-red-500 hover:bg-red-50 rounded-lg text-sm font-bold flex items-center gap-2 transition-colors cursor-pointer"
              >
                <SafeIcon icon={FiTrash2} /> Löschen
              </button>
            ) : <div />}
            <div className="flex gap-3">
              <button type="button" onClick={onClose} className="px-4 py-2 text-gray-400 font-bold text-sm cursor-pointer">Abbrechen</button>
              <button type="submit" className="px-6 py-2 bg-blue-600 text-white rounded-xl font-bold text-sm shadow-lg shadow-blue-200 flex items-center gap-2 cursor-pointer">
                <SafeIcon icon={FiSave} /> Speichern
              </button>
            </div>
          </div>
        </form>
      </motion.div>
    </motion.div>
  );
}

// Hilfsfunktion für die Vorschau
function previewSalutation(contact) {
  if (contact.greetingType === 'team') {
    return `Liebes Team`;
  }

  const nameParts = (contact.name || '').trim().split(/\s+/);
  const lastName = nameParts.length > 1 ? nameParts[nameParts.length - 1] : (nameParts[0] || '...');
  const firstName = nameParts.length > 0 ? nameParts[0] : '...';

  if (contact.greetingType === 'personal') {
    if (contact.useDu) {
      if (contact.gender === 'm') return `Lieber ${firstName}`;
      if (contact.gender === 'f') return `Liebe ${firstName}`;
      return `Liebe/r ${firstName}`;
    } else {
      if (contact.gender === 'm') return `Lieber Herr ${lastName}`;
      if (contact.gender === 'f') return `Liebe Frau ${lastName}`;
      return `Liebe/r ${lastName}`;
    }
  }

  if (contact.greetingType === 'casual') {
    if (contact.useDu) {
      return `Hallo ${firstName}`;
    } else {
      if (contact.gender === 'm') return `Hallo Herr ${lastName}`;
      if (contact.gender === 'f') return `Hallo Frau ${lastName}`;
      return `Hallo ${lastName}`;
    }
  }

  // Fallback / formal
  if (contact.useDu) {
    if (contact.gender === 'm') return `Sehr geehrter ${firstName}`;
    if (contact.gender === 'f') return `Sehr geehrte ${firstName}`;
    return `Sehr geehrte/r ${firstName}`;
  } else {
    if (contact.gender === 'm') return `Sehr geehrter Herr ${lastName}`;
    if (contact.gender === 'f') return `Sehr geehrte Frau ${lastName}`;
    return `Sehr geehrte Damen und Herren`;
  }
}

export default ContactEditModal;